import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import styles from './style.module.scss';
import Image from 'next/image';
import { commercialTestIds } from 'constants/test-constants';
import Video from 'components/video';

import type { CommercialMetricsConfig, CommercialMetrics } from '@tenants';

export type CommercialMetricsProps = {
  metrics: CommercialMetricsConfig;
};

const MetricItem = ({ value, label }: CommercialMetrics) => (
  <div className={styles.metricItem}>
    <div className={styles.metricValue}>{value}</div>
    <div className={styles.metricLabel}>{label}</div>
  </div>
);

const CommercialMetrics = ({ metrics }: CommercialMetricsProps) => {
  const scrollContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollContent = scrollContentRef.current;

    if (!scrollContent) return;

    // Get the width of one set of metrics (half the total content width)
    const totalWidth = scrollContent.offsetWidth;
    const singleSetWidth = totalWidth / 2;

    // Set initial position
    gsap.set(scrollContent, { x: 0 });

    // Create the infinite scroll animation
    const tl = gsap.timeline({ repeat: -1 });

    tl.to(scrollContent, {
      x: -singleSetWidth,
      duration: 15,
      ease: 'none',
    })
      .set(scrollContent, { x: 0 }); // Reset position instantly

    return () => {
      tl.kill();
    };
  }, []);

  return (
    <section className={styles.metricsSection}>
      <Video loop src={metrics.backgroundVideo.src} type={metrics.backgroundVideo.type} className={styles.backgroundVideo} />
      <div className={styles.videoOverlay}></div>
      <div className={styles.container}>
        <div className={styles.logo}>
          <Image src={metrics.logo.src} alt={metrics.logo.alt} width={metrics.logo.width} height={metrics.logo.height} />
        </div>
        <div className={styles.scrollContainer}>
          <div className={styles.scrollContent} ref={scrollContentRef}>
            <div className={styles.metricsGrid}>
              {metrics.metrics.map((metric, index) => (
                <MetricItem
                  key={`original-${index}`}
                  value={metric.value}
                  label={metric.label}
                />
              ))}
            </div>
            {/* Duplicate content for seamless loop */}
            <div data-testid={commercialTestIds.duplicateMetrics} aria-hidden="true" className={styles.metricsGrid}>
              {metrics.metrics.map((metric, index) => (
                <MetricItem
                  key={`duplicate-${index}`}
                  value={metric.value}
                  label={metric.label}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommercialMetrics;
